import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LoanDetail } from '@/types/inventory.types';

interface LentToCounterpartiesCellProps {
  counterparty: string;
  l2cpQuantity?: number;
  l2cpAmountEur?: number | string;
  l2cpRate?: number | string;
  l2cpGrossRate?: number | string;
  l2cpSnpDelta?: number | string;
  loans?: LoanDetail[];
  quantity: number;
  accountId: string;
}

const formatPercent = (value: number | string | undefined): string => {
  const n = value != null ? Number(value) : NaN;
  return Number.isFinite(n) ? `${n.toFixed(2)}%` : '-';
};
const formatCurrency = (value: number | string | undefined): string => {
  const n = value != null ? Number(value) : NaN;
  return Number.isFinite(n) ? `€${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';
};

export const LentToCounterpartiesCell: React.FC<LentToCounterpartiesCellProps> = ({
  counterparty,
  l2cpQuantity,
  l2cpAmountEur,
  l2cpRate,
  l2cpGrossRate,
  l2cpSnpDelta,
  loans = [],
  accountId,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const totalQuantityLent = loans?.reduce((sum, loan) => sum + loan.quantityLent, 0) ?? 0;
  const displayQuantity = l2cpQuantity ?? totalQuantityLent;
  const quantityText =
    displayQuantity != null && typeof displayQuantity === 'number'
      ? displayQuantity.toLocaleString('en-US')
      : '-';
  const totalAmountEUR = loans?.reduce((sum, loan) => sum + loan.amountEUR, 0) ?? 0;
  const displayAmount = l2cpAmountEur ?? totalAmountEUR;
  const hasTooltipData =
    displayQuantity != null ||
    displayAmount != null ||
    l2cpRate != null ||
    l2cpGrossRate != null ||
    l2cpSnpDelta != null;
  const isNostro = /\bnostro\b/i.test(accountId);
  const hasCounterparty = counterparty != null && String(counterparty).trim() !== '';
  const isEmpty = isNostro || !hasCounterparty;

  if (isEmpty) {
    return <span className="text-gray-400">-</span>;
  }

  const handleMouseEnter = () => {
    if (hasTooltipData && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
      setShowTooltip(true);
    }
  };

  return (
    <div className="relative flex justify-start">
      <div
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
        className={hasTooltipData ? 'cursor-pointer transition-colors' : ''}
      >
        <span>
          <span className="font-medium text-blue-600">{counterparty}</span>
          {': '}
          {quantityText}
        </span>
      </div>

      {showTooltip &&
        createPortal(
          <div
            className="fixed z-[9999] min-w-[240px] bg-white border border-gray-200 shadow-xl rounded-lg overflow-hidden p-4"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            <h4 className="text-sm font-semibold text-blue-600 mb-3">{counterparty}</h4>
            <dl className="space-y-2 text-xs">
              <div className="flex justify-between gap-6">
                <dt className="text-gray-500">Quantity:</dt>
                <dd className="text-gray-900 font-medium text-right">
                  {displayQuantity != null ? displayQuantity.toLocaleString('en-US') : '-'}
                </dd>
              </div>
              <div className="flex justify-between gap-6">
                <dt className="text-gray-500">Amount (in EUR):</dt>
                <dd className="text-gray-900 font-medium text-right">{formatCurrency(displayAmount)}</dd>
              </div>
              <div className="flex justify-between gap-6">
                <dt className="text-gray-500">Rate:</dt>
                <dd className="text-gray-900 font-medium text-right">{formatPercent(l2cpRate)}</dd>
              </div>
              <div className="flex justify-between gap-6">
                <dt className="text-gray-500">Gross-Rate:</dt>
                <dd className="text-gray-900 font-medium text-right">{formatPercent(l2cpGrossRate)}</dd>
              </div>
              <div className="flex justify-between gap-6">
                <dt className="text-gray-500">SnP-Delta:</dt>
                <dd className="text-gray-900 font-medium text-right">{formatPercent(l2cpSnpDelta)}</dd>
              </div>
            </dl>
          </div>,
          document.body
        )}
    </div>
  );
};
